import { FC, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridRowHeightParams,
} from "@mui/x-data-grid-pro";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import parseDetailsToArray from "src/utils/Company/Absences/parseDetailsToArray";
import {
  AbsenceRequestHistoryEntryDetailsTypes,
  AbsenceRequestHistoryTableValue,
  HistoryEntryDetailsLabels,
  IAbsenceRequestHistoryEntryDetails,
  IAbsenceRequestHistoryTableEntry,
  IAbsenceRequestRange,
  IEmployeeDetails,
  ITimeAmount,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { EPatternUnit } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import formatDateRange from "src/utils/Company/Absences/formatDateRange";
import formatDayLabel from "src/utils/Company/Absences/formatDayLabel";

interface IAbsenceRequestHistoryDetailsProps {
  data: IAbsenceRequestHistoryEntryDetails;
  billingCycle: EPatternUnit;
}

const formatCellValue = (
  row: IAbsenceRequestHistoryTableEntry,
  value: AbsenceRequestHistoryTableValue,
  billingCycle: EPatternUnit
) => {
  switch (row.id) {
    case AbsenceRequestHistoryEntryDetailsTypes.TYPE:
      return value === EPatternUnit.Hour ? __("Godzinowy") : __("Dzienny");
    case AbsenceRequestHistoryEntryDetailsTypes.RANGE:
      return formatDateRange(billingCycle, value as IAbsenceRequestRange);
    case AbsenceRequestHistoryEntryDetailsTypes.TIME_AMOUNT:
      return billingCycle === EPatternUnit.Day
        ? `${(value as ITimeAmount).days} ${__(
            formatDayLabel((value as ITimeAmount).days)
          )}`
        : `${(value as ITimeAmount).hours}h`;
    case AbsenceRequestHistoryEntryDetailsTypes.APPROVER:
    case AbsenceRequestHistoryEntryDetailsTypes.REPLACER:
      return value
        ? `${(value as IEmployeeDetails).firstName} ${
            (value as IEmployeeDetails).lastName
          }`
        : "-";
    case AbsenceRequestHistoryEntryDetailsTypes.ATTACHMENTS:
      return (
        <List dense>
          {(value as string[]).map((attachment) => (
            <ListItem disableGutters>
              <ListItemText primary={attachment} />
            </ListItem>
          ))}
        </List>
      );
    default:
      return value as string;
  }
};

const AbsenceRequestHistoryDetails: FC<IAbsenceRequestHistoryDetailsProps> = ({
  data,
  billingCycle,
}) => {
  const logStatus = useLogStatus();

  if (!data) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Request history data provided.",
      file: "AbsenceRequestHistoryDetails.tsx",
    });
  }

  if (!billingCycle) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No billingCycle provided.",
      file: "AbsenceRequestHistoryDetails.tsx",
    });
  }

  const rows = parseDetailsToArray(data);

  const columns = useMemo<GridColDef<IAbsenceRequestHistoryTableEntry>[]>(
    () => [
      {
        field: "id",
        headerName: __("Pole"),
        type: "string",
        width: 200,
        valueFormatter: (value) => __(HistoryEntryDetailsLabels[value]),
      },
      {
        field: "before",
        headerName: __("Przed zmianą"),
        type: "string",
        width: 300,
        renderCell: ({ row, value }) =>
          formatCellValue(row, value, billingCycle),
      },
      {
        field: "after",
        headerName: __("Po zmianie"),
        type: "string",
        width: 300,
        renderCell: ({ row, value }) =>
          formatCellValue(row, value, billingCycle),
      },
    ],
    [billingCycle]
  );

  return (
    <Box
      sx={{
        bgcolor: "grey.100",
        p: "1rem",
      }}
    >
      <Typography variant="h6" mb="1rem">
        {__("Szczegóły zmian")}
      </Typography>
      <Paper elevation={2} sx={{ display: "flex", flexDirection: "column" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableColumnSorting
          disableColumnMenu
          disableEval
          hideFooter
          getRowHeight={(params: GridRowHeightParams) =>
            params.id === AbsenceRequestHistoryEntryDetailsTypes.ATTACHMENTS
              ? "auto"
              : undefined
          }
          rowSpacingType="border"
          sx={{
            ".MuiDataGrid-row": {
              borderTop: "1px solid #e0e0e0",
            },
            ".MuiDataGrid-row:nth-of-type(odd)": {
              background: "white",
            },
            ".MuiDataGrid-row--dynamicHeight .MuiDataGrid-cell:nth-child(2)": {
              paddingTop: "1rem",
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default AbsenceRequestHistoryDetails;
